import { useCallback, useEffect, useState } from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router";
import { css } from "emotion";
import * as Sentry from "@sentry/react";
import { useIntl } from "react-intl";
import {
  Navbar,
  NavbarDropdownMenu,
  NavbarDropdownMenuTrigger,
  NavbarFooter,
  NavbarItem,
  NavbarLogoWrapper,
  NavbarUserProfile,
  NavbarUserProfileTrigger,
  UserProfileMenuItem,
  UserProfileSubMenuItemTrigger
} from "@certa/catalyst/layouts/Navbar";
import { BatchImport as BatchImportIcon } from "@certa/icons/components/BatchImport";
import { Tools } from "@certa/icons/components/Tools";

import { getAvatarLetters } from "@certa/blocks/thanos";
import {
  Avatar,
  AvatarColors,
  AvatarSizes
} from "@certa/catalyst/components/Avatar";
import {
  DropdownSubMenu,
  DropdownSubMenuTrigger
} from "@certa/catalyst/components/Dropdown";
import {
  Typography,
  TypographyColors,
  TypographyVariants
} from "@certa/catalyst/components/Typography";

import { SearchDropdown } from "@certa/common/components/SearchDropdown/SearchDropdown";
import {
  TABLEAU_PATH,
  getBatchImportsRoute,
  getBatchUpdatesRoute,
  getSearchRoute,
  getStudioAuditLogsRoute
} from "@certa/common/utils/routes";
import { Book } from "@certa/icons/components/Book";
import { Envelope } from "@certa/icons/components/Envelope";
import { Eye } from "@certa/icons/components/Eye";
import { Gear } from "@certa/icons/components/Gear";
import { Language } from "@certa/icons/components/Language";
import { OpenInWindow } from "@certa/icons/components/OpenInWindow";
import { QuestionOutline } from "@certa/icons/components/QuestionOutline";
import { User } from "@certa/icons/components/User";
import { ChevronRightLight } from "@certa/icons/components/ChevronRightLight";
import { Delegate } from "@certa/icons/components/Delegate";

import { useGetUnReadMentionCount } from "@certa/queries/hooks/mentions.hooks";
import { useGetTaskCount } from "@certa/queries/hooks/tasks.hooks";
import { useUnreadMentionsCountComms } from "@certa/queries/queries/comms";
import { useFlexibleMatch } from "@certa/routing/hooks";
import {
  checkLinkVisibility,
  handleOpenPanelEventsForNav,
  mixpanelHandler
} from "./utils";
import {
  headerNavLinks,
  studioNavLink,
  hermesNavLink
} from "./NavigationSidebar.constants";
import { useMentionsContext } from "@certa/mentions";
import { analyticsService, AnalyticsEvents } from "@certa/analytics";
import { MySubscriptions } from "./components/MySubscriptions";
import { useLanguages } from "./hooks/useLanguages";
import { InstanceSettings } from "./components/InstanceSettings";
import { PermissionTypes, type OptionItem } from "@certa/types";
import { ImpersonationUsersList } from "../Header/components/UserProfile/components/ImpersonationUsersList";
import { useNavigationDrawer } from "./DrawerContext";
import { getIntlBody } from "main/src/js/_helpers/intl-helpers";
import { useHomePage } from "../../hooks/useHomePage";
import { useCanViewTasks } from "../../hooks/useCanViewTasks";
import {
  useCheckPermission,
  useCheckPermissionFn
} from "../../utils/permissions/Chowkidar";
import { useHeaderReportLinkText } from "../../hooks/useHeaderReportLinkText";
import { useEnableUserImpersonation } from "../../hooks/useEnableUserImpersonation";
import { toCapital } from "../../utils/string";
import { getUsersByKind } from "../../utils/helper";
import { useShowHeaderDropdownOptions } from "../../hooks/useShowHeaderDropdownOptions";
import { useCanSeeCreateSupportButton } from "../../hooks/useCanSeeCreateSupportButton";
import { useCreateNewWorkflow } from "../../hooks/useCreateNewWorkflow";
import { CERTA_SUPPORT } from "../../constants";
import type { DropdownOption } from "../../hooks/useFilterHeaderOptionBasedOnUserGroup";
import {
  useCommsNewEndpoints,
  useEnableOS3MassDelegation
} from "../../toggles";
import { useEnableDataObjects } from "../../hooks/useEnableDataObjects";
import { DataObjectTypesDrawer } from "./DataObjectTypesDrawer/DataObjectTypesDrawer";
import { basename } from "@certa/network";
import type { NavItemId } from "../Header/types";
import { ManageDelegations } from "../ManageDelegations/ManageDelegations";
import { useCanViewHermesUI } from "../../hooks/useCanViewHermesUI";
import { useObjectTypeTranslationsManager } from "../../store/dataObjects/useObjectTypeTranslationsManager";
import { useWorkflowInitiator } from "../../hooks/useWorkflowInitiator";
import { StartWorkflowFromObjectDialog } from "@certa/common/StartWorkflowFromObjectDialog";
import { useIsAuditLogsEnabled } from "../../hooks/useCanViewStudio2";
import { useEnableGlobalSearchNext } from "../../toggles/useEnableGlobalSearchNext";
import { useAuth } from "@certa/auth/AuthenticationProvider";
import { useInstanceConfigQuery } from "@certa/queries/hooks/config.hook";
import { useAuthActions } from "@certa/auth/authActions";
import { Sync } from "@certa/icons/components/Sync";

type Link = {
  label: string;
  url: string;
}[];

const useSearchTrackingInfo = () => {
  const shouldUseGlobalSearchNext = useEnableGlobalSearchNext();
  const isDataObjectsEnabled = useEnableDataObjects();

  return {
    certa_version: isDataObjectsEnabled ? "OS3" : "OS2",
    search_version: shouldUseGlobalSearchNext ? "new" : "old"
  };
};

const NavigationSidebar = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const authentication = useAuth();

  const isDataObjectsEnabled = useEnableDataObjects();

  const isMassDelegationEnabled = useEnableOS3MassDelegation();

  useObjectTypeTranslationsManager();

  const location = useLocation();
  const currentRoute = location.pathname;

  useEffect(() => {
    if (currentRoute === "/home") {
      setIsExpanded(true);
    } else {
      setIsExpanded(false);
    }
  }, [currentRoute]);
  const navigate = useNavigate();

  const isSupplierUser = authentication?.user?.kind === 2;
  const isHomePageEnabled = useHomePage();
  const canViewTasks = useCanViewTasks();
  const { data: config } = useInstanceConfigQuery();
  const isStudioEnabled = !!config?.configuration.enable_studio;
  const canViewStudio2Permissions = useCheckPermission(
    PermissionTypes.CAN_ACCESS_STUDIO
  );
  const canViewStudio2 = canViewStudio2Permissions && isStudioEnabled;
  const checkPermissions = useCheckPermissionFn();

  const {
    setIsDashboardDrawerOpen,
    setIsSearchModalOpen,
    isDataObjectTypesDrawerOpen,
    setIsDataObjectTypesDrawerOpen
  } = useNavigationDrawer();

  const filterNavLinks = checkLinkVisibility(checkPermissions, {
    isSupplierUser,
    isHomePageEnabled,
    canViewTasks,
    canViewStudio2,
    canViewDataObjects: isDataObjectsEnabled
  });
  const navLinks = [...headerNavLinks, studioNavLink];

  const activeRoute = navLinks.find(
    nav =>
      currentRoute.includes(nav?.route || "") ||
      nav?.alias?.some(aliasRoute => aliasRoute.test(currentRoute))
  );

  const activeRouteLabel = activeRoute?.label;
  const activeRouteId = activeRoute?.id;

  const { toggleShow: toggleNotificationDrawer } = useMentionsContext();
  const reportText = useHeaderReportLinkText();
  const intl = useIntl();
  const canSeeHermesUI = useCanViewHermesUI();

  // Adding `!!` so that TS understands it as boolean automatically without typecasting since the authentication and user in the reducer are of type `any`
  const shouldShowTableauReports =
    !!authentication?.user?.features?.includes("view_reports") &&
    !!config?.report_embed_url;

  const { data: myTaskCount } = useGetTaskCount();
  const searchTrackingInfo = useSearchTrackingInfo();
  const [shouldShowMySubscriptions, setShouldShowMySubscriptions] =
    useState(false);
  const [shouldShowInstanceSettings, setShouldShowInstanceSettings] =
    useState(false);
  const tableauLinkLabel =
    reportText ||
    intl.formatMessage({
      id: "navigation.tableau",
      defaultMessage: "Open Tableau"
    });

  const handleNavItemsClick = (args: {
    label: string;
    id: NavItemId;
    route?: string;
  }) => {
    const { id, label, route = "" } = args;

    handleOpenPanelEventsForNav(
      label,
      id === "search" ? searchTrackingInfo : undefined
    ); // Todo make this scalable

    switch (id) {
      case "notifications":
        // Close dashboard drawer if open and then open the notifications drawer
        setIsDataObjectTypesDrawerOpen(false);
        setIsDashboardDrawerOpen(false);
        toggleNotificationDrawer();
        break;
      case "search":
        if (!isSearchPage) {
          toggleNotificationDrawer(false);
          setIsDashboardDrawerOpen(false);
          setIsSearchModalOpen(true);
          setIsDataObjectTypesDrawerOpen(false);
        }
        break;
      case "dashboards":
        // Close notifications drawer if open and then open the dashboard drawer
        toggleNotificationDrawer(false);
        setIsDashboardDrawerOpen(prevState => !prevState);
        setIsDataObjectTypesDrawerOpen(false);
        if (activeRouteLabel !== "Dashboards") {
          navigate(route);
        }
        break;
      case "records":
        toggleNotificationDrawer(false);
        setIsDashboardDrawerOpen(false);
        setIsDataObjectTypesDrawerOpen(prevState => !prevState);
        break;
      default:
        toggleNotificationDrawer(false);
        setIsDashboardDrawerOpen(false);
        setIsDataObjectTypesDrawerOpen(false);
        navigate(route);
    }
  };

  const handleStudioClick = () => {
    mixpanelHandler(studioNavLink.label);
    window.open(`${basename}${studioNavLink.route}`, "_blank");
  };

  const handleTableauClick = () => {
    mixpanelHandler("Tableau");
    window.open(`${window.location.origin}${TABLEAU_PATH}`, "_blank");
  };

  const isCommsNewEndpointEnabled = useCommsNewEndpoints();
  const { data: userMentionsCount } = useGetUnReadMentionCount();
  const { data: userMentionsCountComms } = useUnreadMentionsCountComms();

  const showCountBadges = (key: string) => {
    switch (key) {
      case "Notifications":
        return isCommsNewEndpointEnabled
          ? userMentionsCountComms
          : userMentionsCount;
      case "Tasks":
        return myTaskCount;
      default:
        return undefined;
    }
  };
  const userName =
    authentication?.user?.first_name && authentication?.user?.last_name
      ? `${authentication.user.first_name} ${authentication.user.last_name}`
      : "";

  const userEmail = authentication?.user?.email || "";
  const authActions = useAuthActions();

  const canAccessInstanceSettings = useCheckPermission(
    PermissionTypes.CAN_ACCESS_ADMIN_SETTINGS
  );

  const canViewImportPipeline = useCheckPermission(
    PermissionTypes.CAN_VIEW_IMPORT_PIPELINE
  );

  const {
    supportedLanguages,
    preferredLanguage,
    handleLanguageChange,
    renderLanguageMenuItem
  } = useLanguages();

  const canUserViewAs = useEnableUserImpersonation();
  const hasPermissionToImpersonate = useCheckPermission(
    PermissionTypes.CAN_IMPERSONATE_OTHER_USERS
  );

  const tenantName = toCapital(
    (config?.name as string) || "certa"
  ) as Capitalize<string>;

  const usersByKind = getUsersByKind(tenantName);
  const [isSubmenuOpen, setIsSubmenuOpen] = useState(false);

  const handleSubmenuOpenChange = (isOpen: boolean) => {
    setIsSubmenuOpen(isOpen);
  };
  const isSearchPage = useFlexibleMatch(getSearchRoute());

  const userProfileId = authentication?.user?.user_profile_workflow || null;
  const showHeaderDropdownOptions = useShowHeaderDropdownOptions();
  const { supportTicketData, supportTicketKind } =
    useCanSeeCreateSupportButton();
  const { onSelect } = useCreateNewWorkflow();
  const isAuditLogsEnabled = useIsAuditLogsEnabled();

  const {
    objectTypeWorkflowRelations,
    selectedWorkflowKind,
    onSelectWorkflowKind,
    canShowStartWorkflowFromObjectDialog,
    closeStartWorkflowFromObjectDialog,
    handleCreateWorkflow
  } = useWorkflowInitiator({ onClose: () => {}, onCreateWorkflow: onSelect });

  const headerLinks: Link = config?.custom_ui_labels?.header_links || [];

  const footerLinks: Link = config?.custom_ui_labels?.footer_links || [];

  const goToUserProfile = useCallback(() => {
    handleOpenPanelEventsForNav("My Profile");
    navigate(`/process/${userProfileId}`);
  }, [navigate, userProfileId]);

  const goToScheduledReports = () => {
    handleOpenPanelEventsForNav("My Scheduled Reports");
    setShouldShowMySubscriptions(prev => !prev);
  };

  const goToActivityLogs = () => {
    handleOpenPanelEventsForNav("Activity Logs");
    navigate(getStudioAuditLogsRoute());
  };

  const goToInstanceSettings = () => {
    handleOpenPanelEventsForNav("Instance Settings");
    setShouldShowInstanceSettings(prev => !prev);
  };

  const goToImportWorkflows = () => {
    handleOpenPanelEventsForNav("Import Workflows");
    navigate(getBatchImportsRoute());
  };

  const goToBatchUpdates = () => {
    handleOpenPanelEventsForNav("Batch Update Workflows");
    navigate(getBatchUpdatesRoute());
  };

  const myProfileText = intl.formatMessage({
    id: "userProfileMenu.myProfileText",
    defaultMessage: "My Profile"
  });

  const logOutMessage = intl.formatMessage({
    id: "userProfileMenu.logOutMessage",
    defaultMessage: "Log out"
  });

  const languageText = intl.formatMessage({
    id: "userProfileMenu.languageText",
    defaultMessage: "Language"
  });

  const instanceSettingsText = intl.formatMessage({
    id: "loginPageInstances.InstanceSettingsText",
    defaultMessage: "Instance Settings"
  });

  const manageDelegations = intl.formatMessage({
    id: "loginPageInstances.manageDelegations",
    defaultMessage: "Manage Delegations"
  });

  const helpText = intl.formatMessage({
    id: "userProfileMenu.help",
    defaultMessage: "Help"
  });

  const toolsText = intl.formatMessage({
    id: "navigation.tools",
    defaultMessage: "Tools"
  });

  const batchImportWorkflowsText = intl.formatMessage({
    id: "navigation.batchImportWorkflows",
    defaultMessage: "Batch Import Workflows"
  });

  const batchUpdateWorkflowsText = intl.formatMessage({
    id: "navigation.batchUpdateWorkflows",
    defaultMessage: "Batch Update Workflows"
  });

  const onSelectLink = (value: string, index: number) => {
    analyticsService.track(
      AnalyticsEvents.navbar.NAV_PROFILE_HELP_CLICK_OPEN_USER_HELP_LINK,
      // TODO: https://thevetted.atlassian.net/browse/PLAT-21478
      // Passing index to track which link is clicked does not
      // give any useful information to the event
      // To check with the product team if we need this data or not
      { helpLinkIndex: index }
    );
    window.open(value);
  };

  const handleUserProfileMenuClick = () => {
    toggleNotificationDrawer(false);
    setIsDashboardDrawerOpen(false);
  };

  /* We don't show header links to supplier users */
  const shouldShowHeaderLinks = headerLinks?.length > 0 && !isSupplierUser;
  const shouldShowFooterLinks = footerLinks?.length > 0;

  const shouldShowHelpNavMenu =
    showHeaderDropdownOptions.length > 0 ||
    !!supportTicketData ||
    shouldShowHeaderLinks ||
    shouldShowFooterLinks;

  // If mini_logo is available, use it when the navbar is collapsed
  // Otherwise, use the logo
  const logoImageSrc = isExpanded
    ? config?.logo || ""
    : config?.mini_logo || config?.logo;

  const onSelectLanguage = (language: OptionItem) => {
    analyticsService.track(
      AnalyticsEvents.navbar.NAV_PROFILE_LANGUAGE_CLICK_SWITCH_LANGUAGE,
      { language: language.value }
    );
    handleLanguageChange(language.value);
  };

  const [shouldShowManageDelegations, setShouldShowManageDelegations] =
    useState(false);

  const openManageDelegations = () => {
    setShouldShowManageDelegations(prev => !prev);
  };

  const handleSupportTicketClick = () => {
    if (supportTicketKind) onSelectWorkflowKind(supportTicketKind);
  };

  const languageMenuItem = (
    <NavbarDropdownMenu id="language-menu" dropdownContentWidth="16.68rem">
      <NavbarDropdownMenuTrigger>
        <NavbarItem
          id="Language"
          as="div"
          icon={<Language size={16} />}
          label={`${languageText}: ${preferredLanguage}`}
          onClick={() => {}}
          rightIcon={<ChevronRightLight size={16} />}
        />
      </NavbarDropdownMenuTrigger>
      <div className={languageDropdownStyles}>
        {supportedLanguages.map(language => (
          <UserProfileMenuItem
            key={language.value}
            checked={preferredLanguage === language.value}
            onSelect={() => onSelectLanguage(language)}
            text={renderLanguageMenuItem(language)}
            value={language.value}
          />
        ))}
      </div>
    </NavbarDropdownMenu>
  );

  return (
    <>
      <Navbar expanded={isExpanded} activeNavItem={activeRouteId}>
        <NavbarLogoWrapper>
          <RouterLink to="/">
            <img src={logoImageSrc} alt={config?.name || ""} />
          </RouterLink>
        </NavbarLogoWrapper>

        {filterNavLinks(headerNavLinks).map((nav, index) => {
          const { route, label, icon: Icon, id } = nav;
          return (
            <NavbarItem
              id={id}
              onClick={() => handleNavItemsClick({ label, id, route })}
              icon={<Icon size={16} />}
              key={index}
              label={intl.formatMessage({
                id: `navigation.${label}`,
                defaultMessage: label
              })}
              badgeCount={showCountBadges(label)}
            />
          );
        })}
        <NavbarFooter>
          {canViewStudio2 && (
            <NavbarItem
              id={studioNavLink.label}
              onClick={handleStudioClick}
              icon={<studioNavLink.icon size={16} />}
              label={intl.formatMessage({
                id: `navigation.Studio`,
                defaultMessage: studioNavLink.label
              })}
            />
          )}
          {canSeeHermesUI && (
            <NavbarItem
              id={hermesNavLink.label}
              onClick={() =>
                window.open(`${basename}${hermesNavLink.route}`, "_blank")
              }
              icon={<hermesNavLink.icon size={16} />}
              label={intl.formatMessage({
                id: `navigation.Hermes`,
                defaultMessage: hermesNavLink.label
              })}
            />
          )}
          {shouldShowTableauReports && (
            <NavbarItem
              id="Tableau"
              onClick={handleTableauClick}
              icon={<OpenInWindow size={16} />}
              label={tableauLinkLabel}
            />
          )}
          {languageMenuItem}
          {canViewImportPipeline && (
            <NavbarDropdownMenu id="tools-menu" dropdownContentWidth="16.68rem">
              <NavbarDropdownMenuTrigger>
                <NavbarItem
                  id="Tools"
                  as="div"
                  icon={<Tools size={16} />}
                  label={toolsText}
                  onClick={() => {}}
                  rightIcon={<ChevronRightLight size={16} />}
                />
              </NavbarDropdownMenuTrigger>
              <UserProfileMenuItem
                icon={<BatchImportIcon size={12} />}
                onSelect={goToImportWorkflows}
                text={batchImportWorkflowsText}
                value="batch-import-workflows"
              />
              <UserProfileMenuItem
                icon={<Sync size={12} />}
                onSelect={goToBatchUpdates}
                text={batchUpdateWorkflowsText}
                value="batch-update-workflows"
              />
            </NavbarDropdownMenu>
          )}
          {/* Help Dropdown Menu */}
          {shouldShowHelpNavMenu ? (
            <NavbarDropdownMenu id="help-menu">
              <NavbarDropdownMenuTrigger>
                <NavbarItem
                  id="Help"
                  as="div"
                  icon={
                    <QuestionOutline size={16} className={helpIconStyles} />
                  }
                  label={helpText}
                  onClick={() => {}}
                  rightIcon={<ChevronRightLight size={16} />}
                />
              </NavbarDropdownMenuTrigger>
              {!!supportTicketData && (
                <>
                  <UserProfileMenuItem
                    onSelect={handleSupportTicketClick}
                    icon={<Book size={12} />}
                    text={supportTicketData.label}
                    value={CERTA_SUPPORT}
                  />
                </>
              )}
              {showHeaderDropdownOptions.length > 0
                ? showHeaderDropdownOptions.map(
                    (option: DropdownOption, index: number) => (
                      <UserProfileMenuItem
                        key={index}
                        text={option.label}
                        value={option?.url || ""}
                        onSelect={value => window.open(value)}
                      />
                    )
                  )
                : null}
              {shouldShowHeaderLinks
                ? headerLinks.map((link, index) => (
                    <UserProfileMenuItem
                      key={link.url}
                      text={
                        getIntlBody(link, "label") ||
                        getIntlBody(link, "label_en")
                      }
                      value={link.url}
                      onSelect={value => onSelectLink(value, index)}
                    />
                  ))
                : null}
              {shouldShowFooterLinks
                ? footerLinks.map((link, index) => (
                    <UserProfileMenuItem
                      key={link.url}
                      text={
                        getIntlBody(link, "label") ||
                        getIntlBody(link, "label_en")
                      }
                      value={link.url}
                      onSelect={value => onSelectLink(value, index)}
                    />
                  ))
                : null}
            </NavbarDropdownMenu>
          ) : null}
          {/* User profile Dropdown Menu */}
          <NavbarUserProfile
            onClick={handleUserProfileMenuClick}
            id="userprofile-menu"
          >
            <NavbarDropdownMenuTrigger>
              <NavbarUserProfileTrigger
                email={userEmail}
                name={userName}
                onClick={() => handleOpenPanelEventsForNav("Profile")}
                key={"user-profile-menu"}
                data-testid="user-profile-menu"
                avatar={
                  <Avatar
                    aria-label={userName || userEmail}
                    size={AvatarSizes.BIG}
                    color={AvatarColors.BRAND}
                  >
                    {getAvatarLetters(userName || userEmail)}
                  </Avatar>
                }
              />
            </NavbarDropdownMenuTrigger>
            {userProfileId ? (
              <UserProfileMenuItem
                icon={<User size={12} />}
                onSelect={goToUserProfile}
                text={myProfileText}
                value={"profile"}
              />
            ) : null}
            {!isSupplierUser && (
              <UserProfileMenuItem
                icon={<Envelope size={12} />}
                onSelect={goToScheduledReports}
                text={intl.formatMessage({
                  id: "report.mySubscriptions",
                  defaultMessage: "My Scheduled Reports"
                })}
                value="my-scheduled-reports"
              />
            )}
            {isAuditLogsEnabled && (
              <UserProfileMenuItem
                icon={<Book size={12} />}
                onSelect={goToActivityLogs}
                // TODO: Add intl
                text={"Activity Logs"}
                value="activity-logs"
              />
            )}
            {canAccessInstanceSettings && (
              <UserProfileMenuItem
                icon={<Gear size={12} />}
                onSelect={goToInstanceSettings}
                text={instanceSettingsText}
                value="instance-settings"
              />
            )}
            {isMassDelegationEnabled && (
              <UserProfileMenuItem
                icon={<Delegate size={12} />}
                onSelect={openManageDelegations}
                text={manageDelegations}
                value="manage-delegations"
              />
            )}
            {canUserViewAs && hasPermissionToImpersonate && (
              <UserProfileMenuItem
                icon={<Eye size={12} />}
                onSelect={() => {}}
                text={intl.formatMessage({
                  id: "loginPageInstances.viewAs",
                  defaultMessage: "View As"
                })}
                value="view-as"
              >
                <UserProfileSubMenuItemTrigger
                  icon={<Eye size={12} />}
                  text={intl.formatMessage({
                    id: "loginPageInstances.viewAs",
                    defaultMessage: "View As"
                  })}
                />
                {usersByKind.map(user => (
                  <UserProfileMenuItem
                    onSelect={() => {
                      if (user.kind === "Client") {
                        handleOpenPanelEventsForNav(
                          "Impersonate Internal User"
                        );
                      } else {
                        handleOpenPanelEventsForNav(
                          "Impersonate External User"
                        );
                      }
                    }}
                    text={toCapital(user.name)}
                    value={user.name}
                    key={user.name}
                  >
                    <DropdownSubMenu onOpenChange={handleSubmenuOpenChange}>
                      <DropdownSubMenuTrigger>
                        {user.name}
                      </DropdownSubMenuTrigger>
                      <ImpersonationUsersList
                        kind={user.kind}
                        isOpen={isSubmenuOpen}
                      />
                    </DropdownSubMenu>
                  </UserProfileMenuItem>
                ))}
              </UserProfileMenuItem>
            )}

            <UserProfileMenuItem
              onSelect={() => {}}
              text={logOutMessage}
              value="logout"
              preventDropdownClose // Req on logout action, if dropdown closes then it cause double redirection
            >
              <>
                <div className={dividerStyles} />
                <div onClick={authActions.userLogout} className={logOutStyles}>
                  <Typography
                    variant={TypographyVariants.LABEL_SM}
                    color={TypographyColors.NEUTRAL_600}
                  >
                    {logOutMessage}
                  </Typography>
                </div>
              </>
            </UserProfileMenuItem>
          </NavbarUserProfile>
        </NavbarFooter>
      </Navbar>
      <MySubscriptions
        showMySubscriptionsDialog={shouldShowMySubscriptions}
        setShowMySubscriptionsDialog={setShouldShowMySubscriptions}
      />
      <InstanceSettings
        showInstanceSettingsDialog={shouldShowInstanceSettings}
        setShowInstanceSettingsDialog={setShouldShowInstanceSettings}
      />
      {shouldShowManageDelegations && (
        <ManageDelegations
          canShow={shouldShowManageDelegations}
          onClose={() => {
            setShouldShowManageDelegations(false);
          }}
        />
      )}
      {!isSearchPage ? <SearchDropdown /> : null}
      {isDataObjectTypesDrawerOpen && <DataObjectTypesDrawer />}
      {canShowStartWorkflowFromObjectDialog &&
        objectTypeWorkflowRelations &&
        selectedWorkflowKind && (
          <StartWorkflowFromObjectDialog
            dataObjectWorkflowRelations={objectTypeWorkflowRelations}
            workflowKind={selectedWorkflowKind}
            onCreateWorkflow={handleCreateWorkflow}
            onClose={closeStartWorkflowFromObjectDialog}
          />
        )}
    </>
  );
};

// Explicitly defining the width and height of the icons
// to avoid the icon size changing when the text is long
const helpIconStyles = css({
  "> svg": {
    width: "16px",
    height: "16px"
  }
});

const languageDropdownStyles = css({
  maxHeight: "25rem",
  width: "100%",
  overflowY: "auto",
  overflowX: "hidden"
});

const dividerStyles = css({
  height: "1px",
  width: "100%",
  margin: "var(--space-4) 0",
  backgroundColor: "var(--colors-neutral-400)"
});

const logOutStyles = css({
  cursor: "pointer",
  marginBottom: "var(--space-4)",
  padding: "var(--space-12) var(--space-12)",
  "&:hover": {
    backgroundColor: "var(--colors-neutral-300)"
  }
});

const NavigationSidebarWithProfiler = Sentry.withProfiler(NavigationSidebar, {
  name: "NavigationSidebar"
});

export { NavigationSidebarWithProfiler as NavigationSidebar };
